import { useState, ChangeEvent } from 'react'

import { Switch } from 'antd'
import styled from 'styled-components'

import { TemplateModal } from '../TemplateModal'

import { Button } from '@/components/shared-components/BUI/Button'
import { TextArea } from '@/components/shared-components/BUI/Input'
import { pluralise } from '@/utils/pluralise'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const Description = styled.span`
  color: ${({ theme }) => theme.colour.secondary._500};
`

const Label = styled.span`
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
`

export type DuplicateMode = 'name' | 'value'

const getDefaultDescription = (mode: DuplicateMode) => {
  if (mode === 'name') {
    return 'Each line of text below will be used to clone the currently selected item. The text on each line will become the new "name" of the cloned item'
  }

  return 'Each line of text below will be used to clone the currently selected item. The text on each line will become the new "value" of the cloned item'
}

type Props = {
  isModalOpen: boolean
  closeModal: () => void
  onSave: (names: string[], mode: DuplicateMode) => void
  title?: string
  description?: string
  buttonText?: string
  showModeSwitcher?: boolean
}

export const AdvancedDuplicate: React.FC<Props> = ({
  isModalOpen,
  closeModal,
  onSave,
  title = 'Duplicate (advanced)',
  description,
  buttonText = 'Duplicate',
  showModeSwitcher,
}) => {
  const [mode, setMode] = useState<DuplicateMode>('name')
  const [lines, setLines] = useState('')

  const splitLines = lines.split('\n').filter((l) => l.trim().length > 0)

  const updateLines = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setLines(e.target.value)
  }

  const duplicateValues = () => {
    onSave(splitLines, mode)
    onClose()
  }

  const onClose = () => {
    setLines('')
    closeModal()
  }

  return (
    <TemplateModal
      title={title}
      closeModal={onClose}
      isModalOpen={isModalOpen}
      footer={[
        <Button key="add" variant="primary" onClick={duplicateValues}>
          {buttonText} {pluralise(splitLines.length, 'line')}
        </Button>,
      ]}
    >
      <Wrapper>
        {showModeSwitcher && (
          <div>
            <Switch
              unCheckedChildren="Name mode"
              checkedChildren="Value mode"
              checked={mode === 'value'}
              onChange={(checked) => setMode(checked ? 'value' : 'name')}
            />
          </div>
        )}

        <Description>{description ?? getDefaultDescription(mode)}</Description>
        <TextArea
          autoSize={{ minRows: 6, maxRows: 10 }}
          value={lines}
          onInput={updateLines}
        />

        <section>
          <Label>Preview</Label>
          <ul>
            {splitLines.map((line, index) => (
              <li key={`${index}-${line}`}>{line}</li>
            ))}
          </ul>
        </section>
      </Wrapper>
    </TemplateModal>
  )
}
